import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Hub } from '@aws-amplify/core';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';
import { AppProperties } from './app.properties';
import { AlertComponent } from './components/atoms/alert/alert.component';
import { Role } from './config/constants';
import { COPYS, HIDDEN_TEXT, PROPERTIES_REFERENCES } from './constants';
import { AssessmentConfirmationAlertService } from './modules/assessment/services/assessment-confirmation-alert.service';
import { AuthService } from './services/auth.service';
import { CommunicationBetweenComponentsService } from './services/communication-between-components.service';
import { LoadingService } from './services/loading.service';
import { ModalService } from './services/modal.service';
import { RoleService } from './services/role.service';
import { ScriptConfig, ScriptLoaderService } from './services/script-loader.service';
import { ToastService } from './services/toast.service';
import { VersionCheckService } from './services/version-check.service';

const JARVIS_ROLES_GLOBAL = 'JARVIS_CRECI_ROLES';

const JARVIS_SCRIPT_CONFIG: ScriptConfig = {
  id: 'jarvis-chat-creci-script',
  src: 'assets/scriptlet-chat-creci.js',
  rootElementId: 'pragma-chat-root',
  cleanupElementIds: ['jv-fab', 'jv-panel'],
  cleanupSelectors: ['.jv-badge-cursor-tooltip'],
  cleanupGlobalKeys: ['__PRAGMA_CHAT_CRECI_LOADED__', 'PragmaChat', JARVIS_ROLES_GLOBAL],
  dataset: {
    wsUrl: environment.jarvisWsUrl,
    wsUrlEvaluator: environment.jarvisEvaluatorWsUrl ?? '',
    cognitoClientId: environment.jarvisCognitoClientId,
    source: environment.jarvisSource,
  },
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'pragma-growthmap-web';
  isLoading = false;
  subscription = new Subscription();
  private hubUnsubscribe: (() => void) | null = null;

  @ViewChild(AppProperties.modal, {
    read: ViewContainerRef,
    static: true,
  })
  modal!: ViewContainerRef;
  showConfirmationAlert = false;

  constructor(
    public toastService: ToastService,
    public loadingService: LoadingService,
    public readonly modalService: ModalService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    private readonly assessmentConfirmationAlertService: AssessmentConfirmationAlertService,
    private readonly versionCheckService: VersionCheckService,
    private readonly authService: AuthService,
    private readonly scriptLoader: ScriptLoaderService,
    private readonly roleService: RoleService,
    private readonly communicationBetweenComponentsService: CommunicationBetweenComponentsService,
  ) {}

  ngOnInit(): void {
    this.modalService.loadViewContainerRef(this.modal);
    this.modalService.loadComponent(AlertComponent);

    // Load immediately if the user is already authenticated (normal navigation / reload)
    if (environment.jarvisChatEnabled && this.authService.isAuth()) {
      this.loadJarvisChat();
    }

    // Load after Google OAuth redirect: Amplify fires 'signIn' once tokens are
    // written to localStorage, which is after ngOnInit runs on the first load.
    if (environment.jarvisChatEnabled) {
      this.hubUnsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
        if (event === 'signIn') {
          this.loadJarvisChat();
        }
      });
    }

    this.versionCheckService.startChecking();
    
    this.assessmentConfirmationAlertService.showConfirmationAlert$.subscribe(
      showConfirmationAlert => {
        this.showConfirmationAlert = showConfirmationAlert;
      }
    );

    const title = document.title; 
    window.addEventListener(PROPERTIES_REFERENCES.focus, () => {
      document.title = title;
    });
    window.addEventListener(PROPERTIES_REFERENCES.blur, () => {
      if (this.showConfirmationAlert) {
        document.title = COPYS.PleaseComeBackSomeChangesHaveNotBeenSaved;
      }
    });
    document.addEventListener(PROPERTIES_REFERENCES.visibilitychange, () => {
      if (document.visibilityState !== HIDDEN_TEXT && this.showConfirmationAlert) {
        this.assessmentConfirmationAlertService.loadPendingFeedbackModalWithOutActions();
      }
    });
  }

  private async loadJarvisChat(): Promise<void> {
    const roles = ['student'];
    try {
      // The script loads before role.guard fills the cache, so fetch if missing
      const rolesResponse =
        this.communicationBetweenComponentsService.getRole() ?? (await this.roleService.getRole());
      this.communicationBetweenComponentsService.setRole(rolesResponse);
      if (this.communicationBetweenComponentsService.validateRole(rolesResponse, Role.evaluator)) {
        roles.push('evaluator');
      }
    } catch {
      // If permissions fail, the chat still loads as student
    }
    (window as unknown as Record<string, unknown>)[JARVIS_ROLES_GLOBAL] = roles;
    this.scriptLoader.load(JARVIS_SCRIPT_CONFIG, true);
  }

  ngAfterViewInit(): void {
    const loadingServiceShow$ = this.loadingService.show$.subscribe(loading => {
      this.isLoading = loading;
      this.changeDetectorRef.detectChanges();
    });
    this.subscription.add(loadingServiceShow$);
  }

  ngOnDestroy(): void {
    this.hubUnsubscribe?.();
    this.modalService.clearViewContainerRef();
    this.modalService.clearModalRef();
    this.subscription.unsubscribe();
  }
}
